import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from './AdminLayout';
import Toast from '../components/Toast';
import api from '../api';

/**
 * /admin/launches
 *
 * Read-only view into every Clan with a `launch_at` value. Powers load
 * estimation for upcoming launches and post-launch retro visibility.
 *
 * Backed by GET /api/admin/launches?filter=upcoming|just_launched_24h|
 * just_launched_7d|all. Reuses the exact data that already drives the
 * public /launches page — zero new schema, zero writes.
 */
const TABS = [
  { key: 'upcoming',          label: '⏳ Upcoming' },
  { key: 'just_launched_24h', label: '🚀 Last 24h' },
  { key: 'just_launched_7d',  label: '📅 Last 7 days' },
  { key: 'all',               label: 'All' },
];

const AUDIENCE_LABELS = {
  'under-1k':  '<1K',
  '1K-10K':    '1K – 10K',
  '10K-100K':  '10K – 100K',
  '100K-1M':   '100K – 1M',
  '1M+':       '1M+',
};

function fmtCountdown(seconds) {
  if (seconds == null) return '—';
  const s = Math.abs(seconds);
  const days = Math.floor(s / 86400);
  const hrs = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  if (days > 0) return `${days}d ${hrs}h`;
  if (hrs > 0) return `${hrs}h ${mins}m`;
  if (mins > 0) return `${mins}m`;
  return `${s}s`;
}

function fmtUtc(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'UTC', timeZoneName: 'short',
  });
}

export default function AdminLaunches() {
  const [tab, setTab] = useState('upcoming');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ upcoming: 0, just_launched_24h: 0, just_launched_7d: 0, all: 0 });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [playbookEmail, setPlaybookEmail] = useState('');
  const [pbBusy, setPbBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/launches?filter=${tab}&limit=200`);
      setRows(data.rows || []);
      setCounts(data.counts || counts);
    } catch (ex) {
      setToast({ msg: ex?.response?.data?.detail || 'Failed to load launches' });
    } finally {
      setLoading(false);
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);

  const exportCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/launches/export.csv?filter=${tab}`;
    // Open in same tab so browser inherits cookies (admin auth)
    window.location.href = url;
  };

  const totalReminders = useMemo(
    () => rows.reduce((sum, r) => sum + (r.reminders_count || 0), 0),
    [rows]
  );
  const totalBits = useMemo(
    () => rows.reduce((sum, r) => sum + (r.bits_minted || 0), 0),
    [rows]
  );

  const sendPlaybook = async () => {
    const email = playbookEmail.trim();
    if (!email || !email.includes('@')) {
      setToast({ msg: 'Enter a valid email first' });
      return;
    }
    setPbBusy(true);
    try {
      const { data } = await api.post('/admin/playbook/send', { email });
      setToast({ msg: `📘 Playbook sent to ${data.emailed_to}${data.known_user ? ' (known user)' : ''}` });
      setPlaybookEmail('');
    } catch (ex) {
      setToast({ msg: ex?.response?.data?.detail || 'Send failed' });
    } finally {
      setPbBusy(false);
    }
  };

  return (
    <AdminLayout active="launches">
      <div className="admin-page" data-testid="admin-launches-page">
        <div className="admin-page-head">
          <h1 className="admin-page-title">🚀 Launches</h1>
          <div className="admin-page-actions">
            <button className="admin-btn" onClick={exportCsv} disabled={rows.length === 0} data-testid="al-export-csv">
              Export CSV
            </button>
            <button className="admin-btn" onClick={refresh} data-testid="al-refresh">
              Refresh
            </button>
          </div>
        </div>

        <p className="admin-page-sub">
          Every Clan with a scheduled or completed launch. Reminder count is your best proxy
          for expected launch-day load — coordinate directly with the creator when it spikes.
        </p>

        {/* Send Creator Playbook to any email */}
        <div style={{
          display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
          padding: '14px 16px', marginBottom: 20,
          background: 'linear-gradient(135deg, rgba(0,180,255,0.06), rgba(224,64,251,0.06))',
          border: '1px solid rgba(0,180,255,0.15)', borderRadius: 12,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
            📘 Send Creator Playbook to any email
          </div>
          <input
            type="email"
            value={playbookEmail}
            onChange={(e) => setPlaybookEmail(e.target.value)}
            placeholder="creator@example.com"
            style={{
              flex: '1 1 260px', minWidth: 240, padding: '9px 12px',
              borderRadius: 8, border: '1px solid rgba(15,23,42,0.15)',
              background: 'rgba(255,255,255,0.6)', fontSize: 13,
            }}
            data-testid="al-playbook-email-input"
            onKeyDown={(e) => { if (e.key === 'Enter' && !pbBusy) sendPlaybook(); }}
          />
          <button
            className="admin-btn"
            onClick={sendPlaybook}
            disabled={pbBusy || !playbookEmail.trim()}
            data-testid="al-playbook-send-btn"
            style={{ background: 'linear-gradient(135deg,#00B4FF,#E040FB)', color: '#fff', fontWeight: 700, border: 'none' }}
          >
            {pbBusy ? 'Sending…' : 'Send Playbook →'}
          </button>
        </div>

        <div className="admin-tabs" style={{ marginBottom: 16 }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`admin-tab${tab === t.key ? ' active' : ''}`}
              onClick={() => setTab(t.key)}
              data-testid={`al-tab-${t.key}`}
            >
              {t.label}
              <span className="admin-tab-count"> · {counts[t.key] ?? 0}</span>
            </button>
          ))}
        </div>

        {!loading && rows.length > 0 && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14, fontSize: 13 }}>
            <div className="admin-pill" data-testid="al-total-reminders">
              🔔 {totalReminders.toLocaleString()} reminders across this view
            </div>
            <div className="admin-pill" data-testid="al-total-bits">
              💎 {totalBits.toLocaleString()} Bits minted
            </div>
          </div>
        )}

        {loading ? (
          <div className="admin-empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="admin-empty">
            {tab === 'upcoming'
              ? 'No upcoming scheduled launches right now.'
              : 'No Clans match this filter.'}
          </div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table" data-testid="al-table">
              <thead>
                <tr>
                  <th>Creator</th>
                  <th>Audience</th>
                  <th>Launch time (UTC)</th>
                  <th style={{ textAlign: 'right' }}>Reminders</th>
                  <th style={{ textAlign: 'right' }}>Fill</th>
                  <th style={{ textAlign: 'right' }}>Holders</th>
                  <th>Flags</th>
                  <th style={{ textAlign: 'right' }}>Open</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isSqrt = r.curve_type === 'sqrt' || !r.supply_cap;
                  const fillPct = !isSqrt && r.supply_cap
                    ? ((r.bits_minted / r.supply_cap) * 100).toFixed(1)
                    : null;
                  return (
                    <tr key={r.clan_id} data-testid={`al-row-${r.handle}`}>
                      <td>
                        <div style={{ fontWeight: 700 }}>{r.display_name}</div>
                        <div className="admin-small admin-mono">@{r.handle}</div>
                        {r.email && <div className="admin-small admin-muted">{r.email}</div>}
                      </td>
                      <td>
                        <span className="admin-pill">
                          {AUDIENCE_LABELS[r.audience_bucket] || r.audience_bucket || '—'}
                        </span>
                      </td>
                      <td>
                        <div className="admin-mono admin-small">{fmtUtc(r.launch_at)}</div>
                        {r.launch_tz && r.launch_tz !== 'UTC' && r.launch_at && (() => {
                          try {
                            const dt = new Date(r.launch_at);
                            const creatorLocal = dt.toLocaleString(undefined, {
                              year: 'numeric', month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit',
                              timeZone: r.launch_tz, timeZoneName: 'short',
                            });
                            return (
                              <div
                                className="admin-mono admin-small admin-muted"
                                data-testid={`al-launch-tz-${r.handle}`}
                                title={`Creator's local timezone: ${r.launch_tz}`}
                              >
                                {creatorLocal}
                              </div>
                            );
                          } catch { return null; }
                        })()}
                        <div className="admin-small admin-muted">
                          {r.is_upcoming
                            ? `in ${fmtCountdown(r.seconds_to_launch)}`
                            : `${fmtCountdown(r.seconds_since_launch)} ago`}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }} data-testid={`al-reminders-${r.handle}`}>
                        🔔 {r.reminders_count.toLocaleString()}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {isSqrt ? (
                          <>
                            <div className="admin-mono">{r.bits_minted.toLocaleString()}</div>
                            <div className="admin-small admin-muted">of ∞ (sqrt)</div>
                          </>
                        ) : (
                          <>
                            <div className="admin-mono">{r.bits_minted.toLocaleString()} / {r.supply_cap.toLocaleString()}</div>
                            <div className="admin-small admin-muted">{fillPct}%</div>
                          </>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }} className="admin-mono">
                        {r.holders_count.toLocaleString()}
                      </td>
                      <td>
                        {r.verified && <span className="admin-pill" title="Verified creator">✓ Verified</span>}
                        {' '}
                        {r.launch_rescheduled && <span className="admin-pill" title="Creator has used their one reschedule">↻ Resch.</span>}
                        {' '}
                        {r.is_upcoming
                          ? <span className="admin-status admin-status-invited">Scheduled</span>
                          : <span className="admin-status admin-status-onboarded">Launched</span>}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <a
                          href={`/c/${r.handle}`}
                          target="_blank"
                          rel="noreferrer"
                          className="admin-btn-sm"
                          data-testid={`al-open-${r.handle}`}
                        >
                          View →
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toast && <Toast msg={toast.msg} onClose={() => setToast(null)} />}
    </AdminLayout>
  );
}
